import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function IlceAnaliziPage() {
  useAuthGuard();
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState<any[]>([]);
  const [sirala, setSirala] = useState<'rezervasyon' | 'kazanc'>('rezervasyon');
  const [error, setError] = useState('');

  useEffect(() => { setMounted(true); }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('reservations')
      .select('ilce,ucret,para_birimi,durum,gelis_tarihi,alinis_yeri,birak_yeri')
      .neq('durum', 'İptal Edildi');
    if (err) setError(err.message);
    else setReservations(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ilceDetaylari = useMemo(() => {
    const map: Record<string, { rezervasyonSayisi: number; toplamEUR: number; toplamUSD: number; toplamTL: number; rotalar: Record<string, number>; gunler: Record<string, number>; paraBirimi: string }> = {};
    reservations.forEach((r: any) => {
      const ilce = r.ilce || 'Diğer';
      if (!map[ilce]) map[ilce] = { rezervasyonSayisi: 0, toplamEUR: 0, toplamUSD: 0, toplamTL: 0, rotalar: {}, gunler: {}, paraBirimi: r.para_birimi || 'EUR' };
      map[ilce].rezervasyonSayisi++;
      if (r.para_birimi === 'EUR') map[ilce].toplamEUR += Number(r.ucret);
      else if (r.para_birimi === 'USD') map[ilce].toplamUSD += Number(r.ucret);
      else if (r.para_birimi === 'TL') map[ilce].toplamTL += Number(r.ucret);
      const rota = `${r.alinis_yeri} → ${r.birak_yeri}`;
      map[ilce].rotalar[rota] = (map[ilce].rotalar[rota] || 0) + 1;
      const gun = new Date(r.gelis_tarihi).toLocaleDateString('tr-TR', { weekday: 'long' });
      map[ilce].gunler[gun] = (map[ilce].gunler[gun] || 0) + 1;
    });

    return Object.entries(map).map(([ilce, d]) => ({
      ilce,
      rezervasyonSayisi: d.rezervasyonSayisi,
      toplamEUR: Math.round(d.toplamEUR),
      toplamUSD: Math.round(d.toplamUSD),
      toplamTL: Math.round(d.toplamTL),
      paraBirimi: d.paraBirimi,
      enCokRota: Object.entries(d.rotalar).sort((a, b) => b[1] - a[1])[0]?.[0] || '-',
      enYogunGun: Object.entries(d.gunler).sort((a, b) => b[1] - a[1])[0]?.[0] || '-',
    })).sort((a, b) => sirala === 'rezervasyon' ? b.rezervasyonSayisi - a.rezervasyonSayisi : b.toplamEUR - a.toplamEUR);
  }, [reservations, sirala]);

  const grafikData = ilceDetaylari.slice(0, 10).map(i => ({
    ilce: i.ilce, rezervasyon: i.rezervasyonSayisi, kazanc: i.toplamEUR,
  }));

  const toplamRez = ilceDetaylari.reduce((s, i) => s + i.rezervasyonSayisi, 0);
  const toplamEUR = ilceDetaylari.reduce((s, i) => s + i.toplamEUR, 0);

  const tooltipStyle = { backgroundColor: '#1F2937', border: '1px solid rgba(200,164,92,0.2)', borderRadius: '8px', color: '#fff', fontSize: '12px' };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-display font-bold text-white">İlçe / Bölge Analizi</h2>
        <p className="text-sm text-gray-400 mt-1">Bölgesel transfer istatistikleri ve karlılık analizi</p>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-red-400"><i className="ri-error-warning-line mr-2"></i>{error}</div>}

      {loading ? (
        <div className="grid grid-cols-4 gap-4 mb-6">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-vip-dark rounded-lg animate-pulse"></div>)}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Toplam Rezervasyon</p>
              <p className="text-2xl font-bold text-white">{toplamRez}</p>
            </div>
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Toplam Ciro (EUR)</p>
              <p className="text-2xl font-bold text-vip-gold">{toplamEUR.toLocaleString('tr-TR')} €</p>
            </div>
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">En Çok Transfer</p>
              <p className="text-lg font-bold text-blue-400">{ilceDetaylari[0]?.ilce || '-'}</p>
            </div>
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">En Karlı Bölge</p>
              <p className="text-lg font-bold text-emerald-400">{[...ilceDetaylari].sort((a, b) => b.toplamEUR - a.toplamEUR)[0]?.ilce || '-'}</p>
            </div>
          </div>

          <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">İlçe Bazlı Rezervasyon ve Kazanç</h3>
              <div className="flex gap-1">
                {(['rezervasyon','kazanc'] as const).map(s => (
                  <button key={s} onClick={() => setSirala(s)}
                    className={`px-3 py-1 text-xs rounded-md transition-all border ${sirala === s ? 'bg-vip-gold/15 text-vip-gold border-vip-gold/20' : 'text-gray-400 hover:text-white border-transparent'}`}>
                    {s === 'rezervasyon' ? 'Rezervasyona Göre' : 'Kazanca Göre'}
                  </button>
                ))}
              </div>
            </div>
            {mounted && (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={grafikData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                  <XAxis dataKey="ilce" stroke="#6B7280" fontSize={11} angle={-30} textAnchor="end" height={60} />
                  <YAxis yAxisId="left" stroke="#6B7280" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke="#6B7280" fontSize={12} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar yAxisId="left" dataKey="rezervasyon" fill="#C8A45C" radius={[4, 4, 0, 0]} name="Rezervasyon" />
                  <Bar yAxisId="right" dataKey="kazanc" fill="#60A5FA" radius={[4, 4, 0, 0]} name="Kazanç (EUR)" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ilceDetaylari.map((i, idx) => (
              <div key={i.ilce} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-vip-gold/15 flex items-center justify-center text-xs text-vip-gold font-bold">{idx + 1}</span>
                    <h4 className="text-sm font-semibold text-white">{i.ilce}</h4>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div className="bg-vip-black/50 rounded-md p-2">
                    <p className="text-gray-500 mb-0.5">Rezervasyon</p>
                    <p className="text-white font-semibold text-base">{i.rezervasyonSayisi}</p>
                  </div>
                  <div className="bg-vip-black/50 rounded-md p-2">
                    <p className="text-gray-500 mb-0.5">Toplam Kazanç</p>
                    <div>
                      {i.toplamEUR > 0 && <p className="text-vip-gold font-semibold">{i.toplamEUR} €</p>}
                      {i.toplamUSD > 0 && <p className="text-blue-400">{i.toplamUSD} $</p>}
                      {i.toplamTL > 0 && <p className="text-emerald-400">{i.toplamTL.toLocaleString('tr-TR')} ₺</p>}
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">En Çok Rota</span>
                    <span className="text-gray-300 truncate max-w-[150px]">{i.enCokRota}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">En Yoğun Gün</span>
                    <span className="text-gray-300">{i.enYogunGun}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {ilceDetaylari.length === 0 && (
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-8 text-center">
              <p className="text-sm text-gray-400">Henüz veri yok.</p>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}